import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { AppComponent } from '../../app.component';
import { BaseService } from '../../shared/services/base.service';

import { Quote } from '../../shared/models/quote.interface';
import { QuoteNote } from '../../shared/models/quote.note.interface';
import { QuoteSummary } from '../models/quote.summary.interface';

import { Office } from '../../shared/models/office.interface';
import { LeadSourceType } from '../../shared/models/lead.source.type.interface';
import { PaymentType } from '../../shared/models/payment.type.interface';
import { PaymentTerm } from '../../shared/models/payment.term.interface';
import { JobType } from '../../shared/models/job.type.interface';
import { User } from '../../shared/models/user.interface';
import { Customer } from '../../shared/models/customer.interface';
import { ItemType } from '../../shared/models/item.type.interface';
import { RValue } from '../../shared/models/r.value.interface';
import { Area } from '../../shared/models/area.interface';
import { ItemUsage } from '../../shared/models/item.usage.interface';
import { LostReasonType } from '../../shared/models/lost.reason.type.interface';
import { Competitor } from '../../shared/models/competitor.interface';
import { CustomerType } from '../../shared/models/customer.type.interface';
import { QuoteFilter } from '../models/quote.filter';
import { ContactType } from '../../shared/models/contact.type.interface';
import { ProjectCustomerEdit } from '../../shared/models/project.customer.edit.interface';
import { ConsentURL } from '../../shared/models/consenturl.interface';
import { QuoteAreaItem } from '../../shared/models/quote.area.item.interface';
import { QuoteAreaSummary } from '../models/quote.area.summary.interface';
import { ItemMaster } from '../../shared/models/item.master.interface';
import { PrintOptionsDialogData } from '../../shared/controls/print-options-dialog/print.options.dialog';
import { DealerQuoteSummary } from '../list/list.component';
import { QuoteAcknowledgement } from '../../shared/models/quote.acknowledgement.interface';
import { QuoteAcknowledgementAreaItem } from '../../shared/models/quote acknowledgement.area.item.interface';
import { DealerDocument } from '../../shared/models/dealer.document.interface';
import { TaxOptionsDialogData } from '../../shared/models/taxoptions.dialog.interface';
import { PrintChangeOrderOptions } from '../../job/models/print.change.order.options.interface';
import { QuoteArea } from '../../shared/models/quote.area.interface';
import { AreaSummary } from '../models/area.summary.interface';
import { QuoteItemNoteTemplate } from '../../shared/models/quote.item.note.template.interface';

@Injectable()
export class QuoteService extends BaseService {

  constructor(private appComponent: AppComponent, protected http: HttpClient, router: Router) {
    super(http, router);
  }

  getConsentURL(): Observable<ConsentURL> {
    return this.httpGet<ConsentURL>("/quote/consentURL");
  }

  getQuote(quoteId: number): Observable<QuoteSummary> {
    return this.httpGet<QuoteSummary>("/quote/" + quoteId);
  }

  quoteAcknowledgedItems(quoteId: number): Observable<QuoteAcknowledgementAreaItem[]> {
    return this.httpGet<number[]>("/quote/quoteAcknowledgementItems/" + quoteId);
  }

  getCustomers(): Observable<ProjectCustomerEdit[]> {
    return this.httpGet<ProjectCustomerEdit[]>("/quote/customer");
  }

  getCustomer(customerId: number): Observable<Customer> {
    return this.httpGet<Customer>("/customer/" + customerId);
  }

  getCustomerTypes(): Observable<CustomerType[]> {
    return this.httpGet<CustomerType[]>("/customer/customerType");
  }

  getContactTypes(): Observable<ContactType[]> {
    return this.httpGet<ContactType[]>("/customer/contactType");
  }

  getQuoteAreaItem(quoteAreaItemId: number): Observable<QuoteAreaSummary> {
    return this.httpGet<QuoteAreaSummary>("/quote/quoteAreaItem/" + quoteAreaItemId);
  }

  getQuoteNotes(quoteId: number): Observable<QuoteNote[]> {
    return this.httpGet<QuoteNote[]>("/quote/quoteNotes/" + quoteId);
  }

  getQuoteNote(quoteNoteId: number): Observable<QuoteNote> {
    return this.httpGet<QuoteNote>("/quote/quoteNote/" + quoteNoteId);
  }

  getDealerDocuments(activeOnly: boolean): Observable<DealerDocument[]> {
    let params = new HttpParams();

    params = params.append('activeOnly', activeOnly);
    return this.httpGet<DealerDocument[]>("/document?" + params.toString());
  }

  getDocumentAttachment(documentId: number): Observable<Blob> {
    return this.httpGetFile("/document/attachment/" + documentId);
  }

  getQuoteNoteAttachment(quoteNoteId: number): Observable<Blob> {
    return this.httpGetFile("/quote/quoteNote/" + quoteNoteId + "/attachment");
  }

  getQuoteAcknowlegementAttachment(quoteAcknowledgementId: number): Observable<Blob> {
    return this.httpGetFile(`/quote/quoteAcknowledgement/${quoteAcknowledgementId}/attachment`);
  }

  getQuoteUser(id: string): Observable<User> {
    return this.httpGet<User>("user/id");
  }

  getQuoteAcknowledgement(ackHash: string): Observable<QuoteAcknowledgement> {
    return this.httpGet<QuoteAcknowledgement>(`/weblink/acknowledge/${ackHash}`);
  }

  getQuoteAcknowledgements(quoteId: number): Observable<QuoteAcknowledgement[]> {
    return this.httpGet<QuoteAcknowledgement[]>(`/quote/acknowledgements/${quoteId}`);
  }

  getSummary(filter: QuoteFilter): Observable<DealerQuoteSummary> {
    let params = new HttpParams();

    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.dateRange) params = params.append('dateRange', filter.dateRange.toString());
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.statusId) params = params.append('statusId', filter.statusId.toString());

    return this.httpGet<DealerQuoteSummary>("/quote/summary?" + params.toString());
  }

  getList(filter: QuoteFilter, sortColumn: string, sortDirection: string, page: number): Observable<Quote[]> {
    let params = new HttpParams();

    if (page != null) params = params.append('page', page.toString());
    if (sortColumn != null) params = params.append('sortColumn', sortColumn);
    if (sortDirection != null) params = params.append('sortDirection', sortDirection);
    if (filter.projectName) params = params.append('projectName', filter.projectName);
    if (filter.dateRange) params = params.append('dateRange', filter.dateRange.toString());
    if (filter.salespersonId) params = params.append('salespersonId', filter.salespersonId.toString());
    if (filter.jobTypeId) params = params.append('jobTypeId', filter.jobTypeId.toString());
    if (filter.statusId) params = params.append('statusId', filter.statusId.toString());
    
    return this.httpGet<Quote[]>("/quote?" + params.toString());
  }

  getAreas(): Observable<Area[]> {
    return this.httpGet<Area[]>("/quote/area");
  }

  getArea(areaId: number): Observable<Area> {
    return this.httpGet<Area>("/quote/area/" + areaId);
  }

  getOffices(): Observable<Office[]> {
    return this.httpGet<Office[]>("/office");
  }

  getStates(): Observable<string[]> {
    return this.httpGet<string[]>("/admin/states");
  }

  getLeadSourceTypes(): Observable<LeadSourceType[]> {
    return this.httpGet<LeadSourceType[]>("/customer/leadSource");
  }

  getPaymentTypes(): Observable<PaymentType[]> {
    return this.httpGet<PaymentType[]>("/customer/paymentType");
  }

  getPaymentTerms(): Observable<PaymentTerm[]> {
    return this.httpGet<PaymentTerm[]>("/customer/paymentTerm");
  }

  getJobTypes(): Observable<JobType[]> {
    return this.httpGet<JobType[]>("/quote/jobType");
  }

  getQuoteTemplates(): Observable<Quote[]> {
    return this.httpGet<Quote[]>("/quote/template");
  }

  getSalespersons(activeOnly?: boolean): Observable<User[]> {
    if (activeOnly != null) {
      return this.httpGet<User[]>("/user/salesperson?activeOnly=" + activeOnly);
    }
    else {
      return this.httpGet<User[]>("/user/salesperson");
    }
  }

  getLostReasonTypes(): Observable<LostReasonType[]> {
    return this.httpGet<LostReasonType[]>("/quote/lostReason");
  }

  getCompetitors(): Observable<Competitor[]> {
    return this.httpGet<Competitor[]>("/quote/competitor");
  }

  getItemTypes(): Observable<ItemType[]> {
    return this.httpGet<ItemType[]>("/item/itemType");
  }

  getItemUsages(itemId: number): Observable<ItemUsage[]> {
    return this.httpGet<ItemUsage[]>("/item/itemUsage/" + itemId);
  }

  getRValues(itemTypeId: number): Observable<RValue[]> {
    let params = new HttpParams(); 

    if (itemTypeId) params = params.append('itemTypeId', itemTypeId.toString());

    return this.httpGet<RValue[]>("/item/rValue?" + params.toString());
  }

  getQuoteItemNoteTemplates(): Observable<QuoteItemNoteTemplate[]>{
    return this.httpGet<QuoteItemNoteTemplate[]>("/quote/quoteItemNoteTemplates");
  }

  getItem(itemId): Observable<ItemMaster> {
    return this.httpGet<ItemMaster>("/item/" + itemId);
  }

  getItems(itemTypeId: number, rValue: number): Observable<ItemMaster[]> {
    let params = new HttpParams();

    if (itemTypeId) params = params.append('itemTypeId', itemTypeId.toString());
    if (rValue) params = params.append('rValue', rValue.toString());

    return this.httpGet<ItemType[]>("/item?" + params.toString());
  }

  saveQuote(quote: Quote): Observable<number> {
    if (quote.quote_ID == null)
      return this.httpPut<number>("/quote/", quote);
    else
      return this.httpPost<number>("/quote/" + quote.quote_ID, quote);
  }

  deleteQuote(quoteId: number): Observable<number> {
    return this.httpDelete<number>("/quote/" + quoteId);
  }

  convertLead(quoteId: number): Observable<number> {
    return this.httpPost<number>("/lead/convert/" + quoteId, {});
  }

  expireLead(quoteId: number): Observable<number> {
    return this.httpPost<number>("/lead/expire/" + quoteId, {});
  }

  convertQuote(quoteId: number, paymentTypeId: number, paymentTermId: number, bidAmount: number, acceptedAreas: number[]): Observable<number> {
    let data = {
      PaymentType_ID: paymentTypeId,
      PaymentTerm_ID: paymentTermId,
      BidAmount: bidAmount,
      AcceptedAreas: acceptedAreas
    };
    return this.httpPost<number>("/quote/convert/" + quoteId, data);
  }

  updateQuoteBidAmount(quoteId: number, bidAmount: number): Observable<Quote> {
    const params = new HttpParams()
      .set('bidAmount', bidAmount.toString());

    return this.httpPost<Quote>("/quote/bidAmount/" + quoteId + '?' + params.toString(), {});
  }

  updateQuoteMargin(quoteId: number, grossMargin: number): Observable<Quote> {
    const params = new HttpParams()
      .set('grossMargin', grossMargin.toString());

    return this.httpPost<Quote>("/quote/grossMargin/" + quoteId + '?' + params.toString(), {});
  }

  updateQuoteLostStatus(quoteId: number, quote: Quote): Observable<number> {
    return this.httpPost<number>("/quote/lostStatus/" + quoteId, quote);
  }

  recalcQuote(quoteId: number): Observable<number> {
    return this.httpPut<number>(`/quote/recalculate/${quoteId}`, {});
  }

  updateQuoteMaterialRounding(quoteId: number, quoteMaterialRounding: boolean): Observable<number> {
    const params = new HttpParams()
      .set('roundMaterials', quoteMaterialRounding.toString());
    return this.httpPut<number>("/quote/materialsRounding/" + quoteId + '?' + params.toString(), {});
  }

 resequenceQuoteAreas(quoteId: number, quoteAreas: QuoteArea[]): Observable<Quote> {
    return this.httpPut<Quote>(`/quote/quoteArea/resequenceQuoteAreas/${quoteId}`, quoteAreas);
  }

 resequenceQuoteQuoteAreaItems(quoteId: number, quoteAreas: QuoteAreaItem[]): Observable<Quote> {

   return this.httpPut<Quote>(`/quote/quoteArea/resequenceQuoteAreaItems/${quoteId}`, quoteAreas);
  }

  saveQuoteTaxOptions(qto: TaxOptionsDialogData): Observable<number> {
   return this.httpPut<number>(`/quote/${qto.quote_ID}/taxOptions`, qto);
  }

  acknowledgeQuote(quoteId: number, status: number): Observable<number> {
    const params = new HttpParams()
      .set('isAccepted', status);
    return this.httpPut<number>("/quote/acknowledgement/" + quoteId + '?' +  params.toString(), {});
  }

  geteSignatureStatus(quoteId: number): Observable<number> {
    let statusRequest = {
    };

    return this.httpPost<number>("/quote/esignaturestatus/" + quoteId, statusRequest);
  }

  emailQuote(quoteId: number, changeOrderId: number | undefined, contacts: number[], copySender: boolean, ccEmails: string[],
    message: string,
    contractNotes: string,    
    showPricing: boolean, showSquareFootage: boolean, showTaxes: boolean, locale: string, selectedDocuments: number[], groupAreas: boolean): Observable<number> {
    let emailRequest = {
      changeOrderId: changeOrderId,
      contacts: contacts,
      copySender: copySender,
      targetEmails: ccEmails,
      message: message,
      quote_ContractNotes: contractNotes,
      timezone: - 1 * (new Date().getTimezoneOffset()),
      locale: locale,
      showPricing: showPricing,
      showSquareFootage: showSquareFootage,
      dealerDocuments: selectedDocuments,
      showTaxes: showTaxes,
      groupAreas: groupAreas,

    };

    return this.httpPost<number>("/quote/email/" + quoteId, emailRequest);
  }

  emailMaterialsList(quoteId: number, contacts: string[], message: string, showJobAddress:boolean, locale: string,): Observable<number> {
    let emailRequest = {
      changeOrderId: null,
      Timezone: - 1 * (new Date().getTimezoneOffset()),
      contacts: [] ,
      copySender: false,
      message: message,
      showJobAddress: showJobAddress,
      showPricing: false,
      showSquareFootage: false,
      dealerDocuments: [],
      showTaxes: false,
      targetEmails: contacts,
      locale: locale,
      quote_ContractNotes: null,
      sendViaDocuSign: false,
      updateQuote: true,
    };

    return this.httpPost<number>(`/quote/email/materialsList/${quoteId}`, emailRequest);
  }

  copyQuote(quoteId: number, quote: Quote): Observable<number> {
    return this.httpPost<number>("/quote/copy/" + quoteId, quote);
  }

  printQuote(quoteId: number, changeOrderId: number, printOptions: PrintChangeOrderOptions): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .set('timezone', timezone.toString())
      .append('locale', printOptions.locale)
      .append('showPricing', printOptions.showPricing)
      .append('showSquareFootage', printOptions.showSqrFt)
      .append('showTaxes', printOptions.showTax)
      .append('hideLineItemNotes', printOptions.hideLineItemNotes);

    if (changeOrderId) params = params.append('changeOrderId', changeOrderId.toString());
    
    return this.httpGetFile("/report/quoteContract/" + quoteId + '?' + params.toString());
  }

  printQuoteContract(printOptions: PrintOptionsDialogData): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .set('timezone', timezone.toString())
      .append('showPricing', printOptions.showPricing.toString())
      .append('showSquareFootage', printOptions.showSquareFootage.toString())
      .append('showTaxes', printOptions.showTaxes.toString())
      .append('locale', printOptions.locale)
      .append('contractFillColor', printOptions.fillColor)
      .append('groupAreas', printOptions.groupAreas)
      .append('hideLineItemNotes', printOptions.hideLineItemNotes);

    if (printOptions.quote_ContractNotes) params = params.append('quote_ContractNotes', printOptions.quote_ContractNotes);
    if (printOptions.changeOrderId) params = params.append('changeOrderId', printOptions.changeOrderId.toString());

    return this.httpGetFile("/report/quoteContract/" + printOptions.quoteId + '?' + params.toString() );
  }

  quoteAnalysis(quoteId: number, changeOrderId: number, locale: string): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .set('timezone', timezone.toString())
      .append('locale', locale);

    if (changeOrderId) params = params.append('changeOrderId', changeOrderId.toString());

    return this.httpGetFile("/report/quoteAnalysis/" + quoteId + '?' + params.toString());
  }

  materialList(quoteId: number, locale: string, showJobAddress: boolean): Observable<Blob> {
    let timezone = - 1 * (new Date().getTimezoneOffset());

    let params = new HttpParams()
      .set('timezone', timezone.toString())
      .append('locale', locale)
      .append('showJobAddress', showJobAddress);

    return this.httpGetFile("/report/materialList/" + quoteId + '?' + params.toString());
  }

  newQuoteTemplate(quoteId: number, templateName: string): Observable<number> {
    let request = {
      Quote_ID: quoteId,
      QuoteTemplate_Name: templateName
    }

    return this.httpPost<number>("/quote/template/" + quoteId, request);
  }

  saveQuoteAreaItem(quoteId: number, quoteAreaItem: QuoteAreaItem): Observable<number> {

    if (quoteAreaItem.quoteAreaItem_ID == null) {
      if (quoteAreaItem.quoteArea_ID == null) {
        return this.httpPut<number>("/quote/quoteArea/" + quoteId, quoteAreaItem);
      } else {
        return this.httpPut<number>("/quote/quoteAreaUpgrade/" + quoteAreaItem.quoteArea_ID, quoteAreaItem);
      }
    } else
      return this.httpPost<number>("/quote/quoteAreaItem/" + quoteAreaItem.quoteAreaItem_ID, quoteAreaItem);
  }

  deleteQuoteAreaItem(quoteAreaItemId: number): Observable<number> {
    return this.httpDelete<number>("/quote/quoteAreaItem/" + quoteAreaItemId);
  }

  saveQuoteNote(note: QuoteNote): Observable<number> {
    if (note.quoteNote_ID == null)
      return this.httpPut<number>("/quote/quoteNote", note);
    else
      return this.httpPost<number>("/quote/quoteNote/" + note.quoteNote_ID, note);
  }

  deleteQuoteNote(quoteNoteId: number): Observable<number> {
    return this.httpDelete<number>("/quote/quoteNote/" + quoteNoteId);
  }
}
